import {useEffect, useRef, useState} from "react";
import btnScroll from "../../assets/images/home/btn_scroll.png";
import featBgMain from "../../assets/images/feature/feat_bg_main.png";
import featBgMain2x from "../../assets/images/feature/feat_bg_main@2x.png";
import featBgOverlay from "../../assets/images/feature/feat_bg_overlay.png";
import featOverlayRight from "../../assets/images/feature/feat_overlay_right.png";
import featTitleLine from "../../assets/images/feature/feat_title_line.png";
import featTitleLine2x from "../../assets/images/feature/feat_title_line@2x.png";
import commonBtnPreviousDefault from "../../assets/images/commonbtn/common_btn_previous_default.png";
import commonBtnPreviousDefault2x from "../../assets/images/commonbtn/common_btn_previous_default@2x.png";
import commonBtnPreviousHover from "../../assets/images/commonbtn/common_btn_previous_hover.png";
import commonBtnPreviousHover2x from "../../assets/images/commonbtn/common_btn_previous_hover@2x.png";
import commonBtnPreviousDisabled from "../../assets/images/commonbtn/common_btn_previous_disabled.png";
import commonBtnPreviousDisabled2x from "../../assets/images/commonbtn/common_btn_previous_disabled@2x.png";
import commonBtnNextDefault from "../../assets/images/commonbtn/common_btn_next_default.png";
import commonBtnNextDefault2x from "../../assets/images/commonbtn/common_btn_next_default@2x.png";
import commonBtnNextHover from "../../assets/images/commonbtn/common_btn_next_hover.png";
import commonBtnNextHover2x from "../../assets/images/commonbtn/common_btn_next_hover@2x.png";
import commonBtnNextDisabled from "../../assets/images/commonbtn/common_btn_next_disabled.png";
import commonBtnNextDisabled2x from "../../assets/images/commonbtn/common_btn_next_disabled@2x.png";

const FEATURE_TRANSITION_MS = 700;
const FEATURE_DESIGN_WIDTH = 1920;
const FEATURE_DESIGN_HEIGHT = 1080;
const FEATURE_CARD_TOP = 156;
const FEATURE_SWITCH_BOTTOM = 130;
const FEATURE_SWITCH_HEIGHT = 71;
const FEATURE_CARD_SLOT_LEFTS = [0, 394, 603, 812];
const FEATURE_CARD_TO_SWITCH_TOP = FEATURE_DESIGN_HEIGHT
    - FEATURE_SWITCH_BOTTOM
    - FEATURE_SWITCH_HEIGHT
    - FEATURE_CARD_TOP;

const getFeatureCardScale = function () {
    if (typeof window === "undefined") return 1;
    const availableDistanceToSwitch = Math.max(
        0,
        window.innerHeight
            - FEATURE_CARD_TOP
            - FEATURE_SWITCH_BOTTOM
            - FEATURE_SWITCH_HEIGHT
    );
    return Math.min(
        1,
        window.innerWidth / FEATURE_DESIGN_WIDTH,
        availableDistanceToSwitch / FEATURE_CARD_TO_SWITCH_TOP
    );
};

export default function FeaturesSection({featureItems}) {
    const [activeFeatureIndex, setActiveFeatureIndex] = useState(0);
    const [exitingFeatureIndex, setExitingFeatureIndex] = useState(null);
    const [enteringFeatureIndex, setEnteringFeatureIndex] = useState(null);
    const [transitionDirection, setTransitionDirection] = useState(null);
    const [transitionDistance, setTransitionDistance] = useState(1);
    const [featureCardScale, setFeatureCardScale] = useState(getFeatureCardScale);
    const [featureTransitionDuration, setFeatureTransitionDuration] = useState(FEATURE_TRANSITION_MS);
    const transitionTimerRef = useRef(null);
    const animationLockRef = useRef(false);
    const dragStartXRef = useRef(null);
    const dragStartYRef = useRef(null);
    const dragPointerIdRef = useRef(null);
    const suppressClickRef = useRef(false);
    const suppressClickTimerRef = useRef(null);
    const featureCount = featureItems.length;
    const activeFeature = featureItems[activeFeatureIndex];
    const enteringFeature = Number.isInteger(enteringFeatureIndex)
        ? featureItems[enteringFeatureIndex]
        : null;
    const wrappedFeatures = transitionDirection === "next" && Number.isInteger(exitingFeatureIndex)
        ? featureItems.map(function (item, itemIndex) {
            const previousSlotIndex = (itemIndex - exitingFeatureIndex + featureCount) % featureCount;
            if (previousSlotIndex >= transitionDistance) return null;
            return {item, previousSlotIndex};
        }).filter(Boolean)
        : [];

    useEffect(() => {
        return () => {
            if (transitionTimerRef.current) {
                window.clearTimeout(transitionTimerRef.current);
            }
            if (suppressClickTimerRef.current) {
                window.clearTimeout(suppressClickTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        const handleResize = function () {
            setFeatureCardScale(getFeatureCardScale());
        };

        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const startFeatureTransition = function (nextIndex, direction = "direct", durationMultiplier = 1, distance = 1) {
        if (!featureCount || nextIndex === activeFeatureIndex || animationLockRef.current) return;

        const transitionDuration = FEATURE_TRANSITION_MS * durationMultiplier;
        animationLockRef.current = true;
        setFeatureTransitionDuration(transitionDuration);
        setTransitionDirection(direction);
        setTransitionDistance(distance);
        setExitingFeatureIndex(activeFeatureIndex);
        setEnteringFeatureIndex(direction === "prev" ? nextIndex : null);
        setActiveFeatureIndex(nextIndex);

        transitionTimerRef.current = window.setTimeout(function () {
            setExitingFeatureIndex(null);
            setEnteringFeatureIndex(null);
            setTransitionDirection(null);
            setTransitionDistance(1);
            animationLockRef.current = false;
            transitionTimerRef.current = null;
        }, transitionDuration);
    };

    const moveFeatureToFront = function (itemIndex) {
        const distance = (itemIndex - activeFeatureIndex + featureCount) % featureCount;
        const targetDistance = FEATURE_CARD_SLOT_LEFTS[distance] || FEATURE_CARD_SLOT_LEFTS[1];
        const durationMultiplier = targetDistance / FEATURE_CARD_SLOT_LEFTS[1];
        startFeatureTransition(itemIndex, "next", durationMultiplier, distance);
    };

    const handlePrevFeature = function () {
        if (!featureCount) return;
        startFeatureTransition((activeFeatureIndex - 1 + featureCount) % featureCount, "prev", 1, 1);
    };

    const handleNextFeature = function () {
        if (!featureCount) return;
        startFeatureTransition((activeFeatureIndex + 1) % featureCount, "next", 1, 1);
    };

    const handlePointerDown = function (event) {
        if (event.pointerType === "mouse" && event.button !== 0) return;
        dragStartXRef.current = event.clientX;
        dragStartYRef.current = event.clientY;
        dragPointerIdRef.current = event.pointerId;
    };

    const handlePointerUp = function (event) {
        if (dragPointerIdRef.current !== event.pointerId) return;
        const startX = dragStartXRef.current;
        const startY = dragStartYRef.current;
        dragStartXRef.current = null;
        dragStartYRef.current = null;
        dragPointerIdRef.current = null;
        if (startX === null || startY === null) return;
        const deltaX = event.clientX - startX;
        const deltaY = event.clientY - startY;
        if (Math.abs(deltaX) < 56 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.15) return;
        suppressClickRef.current = true;
        if (suppressClickTimerRef.current) window.clearTimeout(suppressClickTimerRef.current);
        suppressClickTimerRef.current = window.setTimeout(function () {
            suppressClickRef.current = false;
            suppressClickTimerRef.current = null;
        }, 0);
        if (deltaX < 0) handleNextFeature();
        else handlePrevFeature();
    };

    const handlePointerCancel = function (event) {
        if (dragPointerIdRef.current !== event.pointerId) return;
        dragStartXRef.current = null;
        dragStartYRef.current = null;
        dragPointerIdRef.current = null;
    };

    return (
        <section className="section features" id="feature" data-nav="游戏特色">
            <div className="feature-design-stage"
                onPointerDown={handlePointerDown}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerCancel}>
                <picture>
                    <source media="(min-width: 1921px)" srcSet={featBgMain2x}/>
                    <img className="feature-bg-main" src={featBgMain} alt="" aria-hidden="true"/>
                </picture>
                <img className="feature-bg-overlay" src={featBgOverlay} alt="" aria-hidden="true"/>

                <div className="feature-content">
                    {activeFeature ? (
                        <div className="feature-stage-info">
                            <picture
                                className="feature-stage-title-picture"
                                style={{
                                    "--feature-title-width": `${activeFeature.titleWidth}px`,
                                    "--feature-title-height": `${activeFeature.titleHeight}px`,
                                    "--feature-title-left": `${activeFeature.titleLeft}px`,
                                    "--feature-title-bottom": `${activeFeature.titleBottom}px`
                                }}
                            >
                                {activeFeature.titleImage2x ? <source media="(min-width: 1921px)" srcSet={activeFeature.titleImage2x}/> : null}
                                <img className="feature-stage-title-image" src={activeFeature.titleImage} alt={activeFeature.title}/>
                            </picture>
                            <picture
                                className="feature-stage-title-line"
                                style={{
                                    "--feature-title-line-width": `${activeFeature.titleLineWidth}px`
                                }}
                            >
                                <source media="(min-width: 1921px)" srcSet={featTitleLine2x}/>
                                <img src={featTitleLine} alt="" aria-hidden="true"/>
                            </picture>
                            <p className="feature-stage-description" aria-live="polite">{activeFeature.description}</p>
                        </div>
                    ) : null}

                    <div
                        className="feature-card-list"
                        aria-label="游戏特色"
                        style={{
                            "--feature-card-scale": featureCardScale,
                            "--feature-transition-duration": `${featureTransitionDuration}ms`
                        }}
                    >
                        {featureItems.map(function (item, itemIndex) {
                            const isActive = itemIndex === activeFeatureIndex;
                            const slotIndex = (itemIndex - activeFeatureIndex + featureCount) % featureCount;
                            const previousSlotIndex = Number.isInteger(exitingFeatureIndex)
                                ? (itemIndex - exitingFeatureIndex + featureCount) % featureCount
                                : slotIndex;
                            const isWrapEntering = transitionDirection === "next" && previousSlotIndex < transitionDistance;
                            const isPrevEntering = transitionDirection === "prev" && itemIndex === enteringFeatureIndex;
                            const isPrevActiveExiting = transitionDirection === "prev" && itemIndex === exitingFeatureIndex;
                            const targetDistance = FEATURE_CARD_SLOT_LEFTS[transitionDistance] || FEATURE_CARD_SLOT_LEFTS[1];
                            const wrapEnterStyle = isWrapEntering ? {
                                "--feature-wrap-enter-start-left": `${FEATURE_CARD_SLOT_LEFTS[slotIndex] + targetDistance}px`,
                                "--feature-wrap-enter-end-left": `${FEATURE_CARD_SLOT_LEFTS[slotIndex]}px`
                            } : undefined;
                            return (
                                <article
                                    className={`feature-card feature-card--slot-${slotIndex}${isActive ? " feature-card--active" : ""}${isWrapEntering ? " feature-card--wrap-enter" : ""}${isPrevEntering ? " feature-card--prev-enter" : ""}${isPrevActiveExiting ? " feature-card--prev-active-exit" : ""}`}
                                    key={item.id}
                                    style={wrapEnterStyle}
                                    tabIndex={0}
                                    aria-current={isActive ? "true" : undefined}
                                    onClick={function () {
                                        if (suppressClickRef.current) return;
                                        moveFeatureToFront(itemIndex);
                                    }}
                                    onKeyDown={function (event) {
                                        if (event.key === "Enter" || event.key === " ") {
                                            event.preventDefault();
                                            moveFeatureToFront(itemIndex);
                                        }
                                    }}
                                >
                                    <img className="feature-card-art" src={item.src} alt={item.alt}/>
                                    <span className="feature-card-tint" aria-hidden="true"></span>
                                </article>
                            );
                        })}
                        {wrappedFeatures.map(function ({item, previousSlotIndex}) {
                            const targetDistance = FEATURE_CARD_SLOT_LEFTS[transitionDistance] || FEATURE_CARD_SLOT_LEFTS[1];
                            const isPreviousActive = previousSlotIndex === 0;
                            return (
                                <div
                                    className="feature-card feature-card--wrap-exit"
                                    aria-hidden="true"
                                    key={`feature-wrap-exit-${item.id}`}
                                    style={{
                                        "--feature-wrap-exit-start-left": `${FEATURE_CARD_SLOT_LEFTS[previousSlotIndex]}px`,
                                        "--feature-wrap-exit-end-left": `${FEATURE_CARD_SLOT_LEFTS[previousSlotIndex] - targetDistance}px`,
                                        "--feature-wrap-exit-start-width": isPreviousActive ? "var(--feature-card-active)" : "var(--feature-card-small)"
                                    }}
                                >
                                    <img className="feature-card-art" src={item.src} alt="" />
                                    {!isPreviousActive ? <span className="feature-card-tint" aria-hidden="true"></span> : null}
                                </div>
                            );
                        })}
                        {transitionDirection === "prev" && enteringFeature ? (
                            <div className="feature-card feature-card--slot-3 feature-card--prev-exit" aria-hidden="true">
                                <img className="feature-card-art" src={enteringFeature.src} alt="" />
                                <span className="feature-card-tint" aria-hidden="true"></span>
                            </div>
                        ) : null}
                        <img className="feature-card-right-overlay" src={featOverlayRight} alt="" aria-hidden="true"/>
                    </div>
                </div>
            </div>

            <div className="feature-switch-actions" aria-label="切换游戏特色">
                <button className="feature-switch-btn feature-switch-prev" type="button" aria-label="上一张特色玩法" disabled={exitingFeatureIndex !== null} onClick={handlePrevFeature}>
                    <picture><source media="(min-width: 1921px)" srcSet={commonBtnPreviousDefault2x}/><img className="default" src={commonBtnPreviousDefault} alt="" aria-hidden="true"/></picture>
                    <picture><source media="(min-width: 1921px)" srcSet={commonBtnPreviousHover2x}/><img className="active" src={commonBtnPreviousHover} alt="" aria-hidden="true"/></picture>
                    <picture><source media="(min-width: 1921px)" srcSet={commonBtnPreviousDisabled2x}/><img className="disabled" src={commonBtnPreviousDisabled} alt="" aria-hidden="true"/></picture>
                </button>
                <button className="feature-switch-btn feature-switch-next" type="button" aria-label="下一张特色玩法" disabled={exitingFeatureIndex !== null} onClick={handleNextFeature}>
                    <picture><source media="(min-width: 1921px)" srcSet={commonBtnNextDefault2x}/><img className="default" src={commonBtnNextDefault} alt="" aria-hidden="true"/></picture>
                    <picture><source media="(min-width: 1921px)" srcSet={commonBtnNextHover2x}/><img className="active" src={commonBtnNextHover} alt="" aria-hidden="true"/></picture>
                    <picture><source media="(min-width: 1921px)" srcSet={commonBtnNextDisabled2x}/><img className="disabled" src={commonBtnNextDisabled} alt="" aria-hidden="true"/></picture>
                </button>
            </div>

            <div className="home-scroll-btn" aria-hidden="true">
                <img src={btnScroll} alt="" aria-hidden="true"/>
            </div>
        </section>
    );
}
